import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(e.target as Node) &&
        !(e.target as HTMLElement).closest('.hamburger-btn')
      ) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCategorySelect = (url: string) => {
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <a href="/" className="flex items-center gap-2 flex-shrink-0">
              <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-lg bg-primary-500">
                <i className="ri-database-2-line text-background-50 text-lg"></i>
              </div>
              <span className="font-heading text-lg md:text-xl font-bold text-foreground-900 whitespace-nowrap">
                Hipobuy Sheet
              </span>
            </a>

            {/* Desktop nav items */}
            <div className="hidden md:flex items-center gap-2 md:gap-3">
              {/* Categories dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center gap-1.5 px-3 md:px-4 py-2 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-700 hover:bg-background-200 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-grid-line text-base"></i>
                  <span className="hidden sm:inline">Categories</span>
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {isDropdownOpen && (
                  <div className="absolute top-full mt-2 right-0 w-64 bg-background-50 border border-background-200/70 rounded-xl overflow-hidden shadow-lg z-50 max-h-[420px] overflow-y-auto">
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => handleCategorySelect(cat.url)}
                        className="w-full text-left px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 transition-colors duration-150 flex items-center justify-between cursor-pointer"
                      >
                        <span>{cat.name}</span>
                        <i className="ri-external-link-line text-foreground-400 text-xs"></i>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-foreground-900 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                hipobuy sheet
                <i className="ri-arrow-right-up-line text-sm"></i>
              </a>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                buy it now
                <i className="ri-shopping-bag-3-line text-sm"></i>
              </a>
            </div>

            {/* Mobile hamburger + CTA */}
            <div className="flex md:hidden items-center gap-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary-500 text-background-50 dark:text-foreground-950 text-xs font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                buy it now
              </a>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="hamburger-btn w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 border border-background-200/70 text-foreground-700 hover:bg-background-200 transition-colors duration-200 cursor-pointer"
                aria-label="Toggle menu"
              >
                <i className={`text-lg ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile menu overlay */}
      <div
        className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-300 md:hidden ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      {/* Mobile slide-out menu */}
      <div
        ref={mobileMenuRef}
        className={`fixed top-0 right-0 h-full w-[300px] max-w-[85vw] bg-background-50 z-50 transform transition-transform duration-300 ease-in-out md:hidden shadow-xl ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Mobile menu header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-primary-500">
                <i className="ri-database-2-line text-background-50 text-sm"></i>
              </div>
              <span className="font-heading text-base font-bold text-foreground-900">
                Hipobuy Sheet
              </span>
            </div>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-close-line text-foreground-700 text-lg"></i>
            </button>
          </div>

          {/* Mobile categories */}
          <div className="flex-1 overflow-y-auto py-2">
            <div className="px-4 py-2">
              <span className="text-xs font-semibold text-foreground-400 uppercase tracking-wider">
                Product Categories
              </span>
            </div>
            <div className="space-y-0.5 px-2">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategorySelect(cat.url)}
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 active:bg-background-200 transition-colors duration-150 cursor-pointer"
                >
                  <span>{cat.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-foreground-400">
                      {cat.id === 'shoes' ? 'Top Pick' : ''}
                    </span>
                    <i className="ri-external-link-line text-foreground-400 text-xs"></i>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Mobile CTAs */}
          <div className="border-t border-background-200 px-4 py-4 space-y-2.5">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-lg bg-foreground-900 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              hipobuy sheet
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-lg bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              buy it now
              <i className="ri-shopping-bag-3-line text-sm"></i>
            </a>
            <a
              href="/"
              className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-lg border border-background-200 text-foreground-700 text-sm font-medium hover:bg-background-100 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-4-line text-sm"></i>
              Back to Homepage
            </a>
          </div>
        </div>
      </div>
    </>
  );
}